import { Currency, useIsMobile } from '@brunommsilva/react-foundations';
import {
  Box,
  Card,
  CardContent,
  Divider,
  Grid,
  Skeleton,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import useAccountsSummary from '../../hooks/accounts/use-accounts-summary';
import { AccountType } from '../../models/accounts';
import {
  Account as AccountIcon,
  Bank as BankIcon,
  Cash as CashIcon,
  Investment as InvestmentIcon,
  Savings as SavingsIcon,
} from '../icons';

const TotalBalance = () => {
  const { t } = useTranslation();
  const { summary, isLoading } = useAccountsSummary();
  const isMobile = useIsMobile();

  const getIconForType = (type: AccountType, size = 28) => {
    switch (type) {
      case AccountType.BANK:
        return <BankIcon sx={{ color: 'primary.main', fontSize: size }} />;
      case AccountType.SAVINGS:
        return <SavingsIcon sx={{ color: 'success.main', fontSize: size }} />;
      case AccountType.CASH:
        return <CashIcon sx={{ color: 'text.secondary', fontSize: size }} />;
      case AccountType.INVESTMENT:
        return (
          <InvestmentIcon sx={{ color: 'warning.main', fontSize: size }} />
        );
      default:
        return <AccountIcon sx={{ color: 'text.secondary', fontSize: size }} />;
    }
  };

  const getPercentage = (balance: number) => {
    if (!summary.totalBalance) return 0;
    return Math.round((balance / summary.totalBalance) * 100 * 100) / 100;
  };

  const renderSkeletonCard = (key: number) => (
    <Grid
      key={key}
      size={{
        xs: 12,
        sm: 6,
        md: 4,
        lg: 2.4,
      }}
    >
      <Card variant="outlined" sx={{ height: '100%' }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1,
            }}
          >
            <Skeleton variant="text" width="60%" height={20} />
            <Skeleton variant="circular" width={28} height={28} />
          </Box>
          <Skeleton variant="text" width="85%" height={32} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="45%" height={16} />
        </CardContent>
      </Card>
    </Grid>
  );

  // On a phone, five cards one under the other are taller than the screen, and push the accounts out of it.
  const renderCompactCard = () => (
    <Card variant="outlined">
      <CardContent sx={{ px: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography
              variant="overline"
              sx={{
                color: 'text.secondary',
                fontWeight: 'bold',
                lineHeight: 1.5,
              }}
            >
              {t('TOTAL_BALANCE')}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {summary.accountsCount} {t('ACCOUNTS').toLowerCase()}
            </Typography>
          </Box>
          <Typography
            variant="h6"
            component="div"
            noWrap
            sx={{ fontWeight: 'bold' }}
          >
            <Currency amount={summary.totalBalance} />
          </Typography>
        </Box>
        <Divider sx={{ my: 0.75 }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          {summary.balancesByAccountType.map(({ accountType, balance }) => (
            <Box
              key={accountType}
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
            >
              {getIconForType(accountType, 18)}
              <Typography variant="body2" sx={{ flex: 1, lineHeight: 1.6 }}>
                {t(`ACCOUNT_TYPE_${accountType}`)}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {getPercentage(balance)}%
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 'bold',
                  lineHeight: 1.6,
                  minWidth: 96,
                  textAlign: 'right',
                }}
              >
                <Currency amount={balance} />
              </Typography>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );

  if (isLoading && isMobile) {
    return <Skeleton variant="rounded" height={170} />;
  }

  if (isMobile) {
    return renderCompactCard();
  }

  if (isLoading) {
    return (
      <Grid container spacing={2}>
        {Array.from({ length: 5 }).map((_, index) => renderSkeletonCard(index))}
      </Grid>
    );
  }

  return (
    <Grid container spacing={2}>
      {/* Total Overall Card */}
      <Grid
        size={{
          xs: 12,
          sm: 6,
          md: 4,
          lg: 2.4,
        }}
      >
        <Card variant="outlined" sx={{ height: '100%' }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1,
              }}
            >
              <Typography
                variant="overline"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 'bold',
                }}
              >
                {t('TOTAL_BALANCE')}
              </Typography>
              <AccountIcon sx={{ color: 'secondary.main', fontSize: 28 }} />
            </Box>
            <Typography
              variant="h5"
              component="div"
              noWrap
              sx={{
                fontWeight: 'bold',
              }}
            >
              <Currency amount={summary.totalBalance} />
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              {summary.accountsCount} {t('ACCOUNTS').toLowerCase()}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* Account Type Cards */}
      {summary.balancesByAccountType.map(({ accountType, balance }) => (
        <Grid
          key={accountType}
          size={{
            xs: 12,
            sm: 6,
            md: 4,
            lg: 2.4,
          }}
        >
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1,
                }}
              >
                <Typography
                  variant="overline"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 'bold',
                  }}
                >
                  {t(`ACCOUNT_TYPE_${accountType}`)}
                </Typography>
                {getIconForType(accountType)}
              </Box>
              <Typography
                variant="h5"
                component="div"
                noWrap
                sx={{
                  fontWeight: 'bold',
                }}
              >
                <Currency amount={balance} />
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {getPercentage(balance)}% {t('TOTAL')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default TotalBalance;
